import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Confirm, EmptyState, useMeta, KpiCard } from "../components/Shared";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Printer, Play, CheckCircle2, XCircle, Copy, Wallet, Clock, CheckCircle, Repeat, AlertTriangle, Ban, CalendarClock, Eye, Trash2, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";
import PhoneField from "../components/PhoneField";
import { isValidEmail, isValidPhone } from "../lib/validate";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  in_process: { label: "In Process", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  completed: { label: "Completed", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  cancelled: { label: "Cancelled", cls: "bg-slate-100 text-slate-500 border-slate-200" },
};
const empty = { beneficiary_name: "", beneficiary_type: "staff", beneficiary_email: "", beneficiary_phone: "", purpose: "", project_id: "", amount: 0, due_date: "", payment_method: "", notes: "", recurring: false, allowance_ref: "" };

export default function Checks() {
  const { isRole } = useAuth();
  const meta = useMeta();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [emailErr, setEmailErr] = useState("");
  const [phoneErr, setPhoneErr] = useState("");
  const [completeFor, setCompleteFor] = useState(null);
  const [viewFor, setViewFor] = useState(null);
  const [payInfo, setPayInfo] = useState({ payment_method: "", teller: "" });
  const isCeo = isRole(ROLES.CEO);
  const isFm = isRole(ROLES.FM);
  const canCreate = isRole(ROLES.CEO, ROLES.ACEO);
  const methods = meta.payment_methods || ["Cash", "Bank Transfer", "Mobile Money", "Cheque", "Other"];
  const purposes = meta.check_purposes || ["Salary", "Allowance", "Transport", "Utilities", "Others"];

  const qc = useQueryClient();
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["checks"], queryFn: () => api.get("/checks").then((r) => r.data) });
  const { data: summary = null } = useQuery({ queryKey: ["checks-summary"], queryFn: () => api.get("/checks/summary").then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("prefill") === "1") {
      setEditing(null); setEmailErr(""); setPhoneErr("");
      setForm({
        ...empty,
        beneficiary_name: searchParams.get("name") || "",
        beneficiary_type: "staff",
        amount: Number(searchParams.get("amount")) || 0,
        purpose: searchParams.get("purpose") || "",
        notes: searchParams.get("note") || "",
        project_id: searchParams.get("project") || "",
        allowance_ref: searchParams.get("aref") || "",
      });
      setOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setEmailErr(""); setPhoneErr(""); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setEmailErr(""); setPhoneErr("");
    setForm({ ...empty, ...c, project_id: c.project_id || "", due_date: (c.due_date || "").slice(0, 10), amount: c.amount, beneficiary_type: c.beneficiary_type || (employees.some((e) => e.name === c.beneficiary_name) ? "staff" : "non_staff") });
    setOpen(true);
  };

  const save = async () => {
    if (form.beneficiary_email && !isValidEmail(form.beneficiary_email)) { setEmailErr("Enter a valid email address."); toast.error("Please fix the beneficiary email."); return; }
    if (form.beneficiary_phone && !isValidPhone(form.beneficiary_phone)) { setPhoneErr("Enter a valid phone number."); toast.error("Please fix the beneficiary phone."); return; }
    setEmailErr(""); setPhoneErr("");
    if (!form.project_id) { toast.error("Please select a project to debit for this check."); return; }
    try {
      const payload = { ...form, amount: Number(form.amount) || 0, project_id: form.project_id };
      let resp = null;
      if (editing) { await api.put(`/checks/${editing.id}`, payload); }
      else { resp = (await api.post("/checks", payload)).data; }
      if (resp?.pending_approval) toast.info(resp.message || "Submitted to the CEO for approval");
      else toast.success(editing ? "Check updated" : "Check issued to the finance manager");
      setOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const act = async (id, verb, body) => {
    try { await api.post(`/checks/${id}/${verb}`, body || {}); toast.success("Check updated"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doComplete = async () => {
    if (!payInfo.payment_method) { toast.error("Select a payment method"); return; }
    await act(completeFor.id, "complete", payInfo);
    setCompleteFor(null); setPayInfo({ payment_date: "", payment_method: "", teller: "" });
  };

  const printPdf = (c) => downloadExport(`/export/check/${c.id}/pdf`, `payment_check_${(c.id || "").slice(0, 8)}.pdf`);

  const duplicate = async (c) => {
    try {
      const { data } = await api.post(`/checks/${c.id}/duplicate`);
      toast.success(`Duplicated as ${data.check_number}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/checks/${c.id}`); toast.success(`Check ${c.check_number} deleted`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const shown = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <div>
      <PageHeader title="Payment Checks" subtitle="Issue, track and settle official payment checks">
        <div className="flex items-center gap-2">
          <ExportMenu dataset="checks" filename="stems_checks" testId="checks-export" />
          {canCreate && (
            <Button data-testid="new-check-button" onClick={openNew} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">
              <Plus className="h-4 w-4 mr-2" /> New Check
            </Button>
          )}
        </div>
      </PageHeader>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="check-analytics">
          <KpiCard title={`Total Issued · ${summary.totals.issued_count} check(s)`} icon={Wallet} accent="text-slate-900" testId="kpi-issued">
            {fmtFCFA(summary.totals.issued_value)}
          </KpiCard>
          <KpiCard title="Pending / In Process" icon={Clock} accent="text-amber-600" testId="kpi-pending">
            {fmtFCFA(summary.totals.pending_value)}
          </KpiCard>
          <KpiCard title="Paid" icon={CheckCircle} accent="text-emerald-600" testId="kpi-paid">
            {fmtFCFA(summary.totals.paid_value)}
          </KpiCard>
        </div>
      )}

      {summary?.by_unit?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6" data-testid="check-by-unit">
          <div className="px-5 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700">Checks by Business Unit</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Business Unit</TableHead><TableHead className="text-right">Issued</TableHead>
                <TableHead className="text-right">Pending</TableHead><TableHead className="text-right">Paid</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.by_unit.map((u) => (
                <TableRow key={u.company_id} data-testid={`unit-row-${u.company_id}`}>
                  <TableCell className="text-slate-700">{u.company_name}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(u.issued_value)}</TableCell>
                  <TableCell className="text-right font-mono-num text-amber-600">{fmtFCFA(u.pending_value)}</TableCell>
                  <TableCell className="text-right font-mono-num text-emerald-600">{fmtFCFA(u.paid_value)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {summary && (summary.aging.d0_7.count + summary.aging.d8_30.count + summary.aging.d30p.count) > 0 && (
        <div className="mb-6" data-testid="check-aging">
          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-700">
            <AlertTriangle className="h-4 w-4 text-red-500" /> Overdue Aging — settle the oldest first
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[["0–7 days", "d0_7", "text-amber-600"], ["8–30 days", "d8_30", "text-orange-600"], ["30+ days", "d30p", "text-red-600"]].map(([lbl, key, cls]) => (
              <div key={key} data-testid={`aging-${key}`} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm">
                <div className="text-xs uppercase tracking-wide text-slate-400">{lbl} overdue</div>
                <div className={`mt-2 text-2xl font-extrabold font-mono-num ${cls}`}>{fmtFCFA(summary.aging[key].value)}</div>
                <div className="text-xs text-slate-500 mt-1">{summary.aging[key].count} check(s)</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary?.trend?.some((t) => t.issued || t.paid) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6" data-testid="check-trend">
          <div className="text-sm font-semibold text-slate-700 mb-3">Issued vs Paid — last 6 months</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={summary.trend}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" />
              <XAxis dataKey="month" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `${Math.round(v / 1000)}k`} width={40} />
              <Tooltip formatter={(v) => fmtFCFA(v)} />
              <Legend />
              <Bar dataKey="issued" name="Issued" fill="#1E3A8A" radius={[4, 4, 0, 0]} />
              <Bar dataKey="paid" name="Paid" fill="#059669" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {summary?.recurring?.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6" data-testid="check-recurring-upcoming">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <CalendarClock className="h-4 w-4 text-violet-600" /> Upcoming Auto-Raised Checks
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Template</TableHead><TableHead>Beneficiary</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Next Raise</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {summary.recurring.map((r) => (
                <TableRow key={r.id} data-testid={`recurring-upcoming-${r.id}`}>
                  <TableCell className="font-mono-num text-slate-800">{r.check_number}</TableCell>
                  <TableCell className="text-slate-700">{r.beneficiary_name}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(r.amount)}</TableCell>
                  <TableCell className="text-violet-700 font-medium">{fmtDate(r.next_date)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4" data-testid="check-filters">
        {["all", "pending", "in_process", "completed", "cancelled"].map((f) => (
          <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-lg text-sm border capitalize ${filter === f ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white text-slate-600 border-slate-200"}`}>
            {f === "all" ? "All" : STATUS[f].label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {rowsLoading ? <TableSkeleton cols={8} /> : shown.length === 0 ? <EmptyState text="No payment checks yet." /> : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Check No</TableHead><TableHead>Beneficiary</TableHead><TableHead>Purpose</TableHead>
                <TableHead>Project</TableHead><TableHead className="text-right">Amount</TableHead>
                <TableHead>Issued</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((c) => (
                <TableRow key={c.id} data-testid={`check-row-${c.id}`}>
                  <TableCell className="font-mono-num text-slate-800">
                    <div className="flex items-center gap-1.5">{c.check_number}
                      {c.recurring && (
                        <span title="Repeats monthly" data-testid={`recurring-badge-${c.id}`}
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">
                          <Repeat className="h-3 w-3" /> Monthly
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-700">{c.beneficiary_name}</TableCell>
                  <TableCell className="text-slate-600 max-w-[200px] truncate">{c.purpose}</TableCell>
                  <TableCell className="text-slate-600">{c.project_name || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA(c.amount)}</TableCell>
                  <TableCell className="text-slate-500 text-sm">{fmtDate(c.date_issued)}</TableCell>
                  <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS[c.status]?.cls}`} data-testid={`check-status-${c.id}`}>{STATUS[c.status]?.label}</span></TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="icon" variant="ghost" data-testid={`view-check-${c.id}`} onClick={() => setViewFor(c)} title="View details"><Eye className="h-4 w-4" /></Button>
                      {isFm && c.status !== "cancelled" && (
                        <Button size="icon" variant="ghost" className="text-amber-600" data-testid={`momo-check-${c.id}`} title="Pay via Mobile Money"
                          onClick={() => navigate(`/mobile-money?prefill=1&name=${encodeURIComponent(c.beneficiary_name || "")}&phone=${encodeURIComponent(c.beneficiary_phone || "")}&amount=${Math.round(c.amount || 0)}&source_type=${c.purpose === "Salary" ? "Salary" : "Allowance"}&source_label=${encodeURIComponent(c.purpose || "")}`)}>
                          <Smartphone className="h-4 w-4" />
                        </Button>
                      )}
                      {isCeo && ["pending", "in_process"].includes(c.status) && (
                        <Button size="icon" variant="ghost" data-testid={`edit-check-${c.id}`} onClick={() => openEdit(c)} title="Edit"><Pencil className="h-4 w-4" /></Button>
                      )}
                      {isCeo && (
                        <Button size="icon" variant="ghost" data-testid={`duplicate-check-${c.id}`} onClick={() => duplicate(c)} title="Duplicate as new check"><Copy className="h-4 w-4" /></Button>
                      )}
                      {isCeo && c.recurring && (
                        <Confirm onConfirm={() => act(c.id, "stop-recurring")} title="Stop repeating?" actionLabel="Stop repeating"
                          description={`Stop auto-raising ${c.check_number} every month? Existing checks are kept.`}
                          trigger={<Button size="icon" variant="ghost" className="text-violet-600" data-testid={`stop-recurring-${c.id}`} title="Stop monthly repeat"><Ban className="h-4 w-4" /></Button>} />
                      )}
                      {isFm && c.status === "pending" && (
                        <Button size="icon" variant="ghost" className="text-blue-600" data-testid={`start-check-${c.id}`} onClick={() => act(c.id, "start")} title="Start processing"><Play className="h-4 w-4" /></Button>
                      )}
                      {isFm && ["pending", "in_process"].includes(c.status) && (
                        <Button size="icon" variant="ghost" className="text-emerald-600" data-testid={`complete-check-${c.id}`} onClick={() => { setCompleteFor(c); setPayInfo({ payment_method: c.payment_method || "", teller: "" }); }} title="Mark completed"><CheckCircle2 className="h-4 w-4" /></Button>
                      )}
                      {isCeo && c.status !== "completed" && c.status !== "cancelled" && (
                        <Confirm onConfirm={() => act(c.id, "cancel")} title="Cancel check?"
                          description={`Cancel check ${c.check_number}? This cannot be undone.`}
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`cancel-check-${c.id}`} title="Cancel"><XCircle className="h-4 w-4" /></Button>} />
                      )}
                      <Button size="icon" variant="ghost" data-testid={`print-check-${c.id}`} onClick={() => printPdf(c)} title="Print / download PDF"><Printer className="h-4 w-4" /></Button>
                      {isCeo && (
                        <Confirm onConfirm={() => remove(c)} title="Delete check?"
                          description={`Permanently delete ${c.check_number}? ${c.status === "completed" ? "Its recorded expenditure will also be removed. " : ""}This cannot be undone.`}
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-check-${c.id}`} title="Delete"><Trash2 className="h-4 w-4" /></Button>} />
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${editing.check_number}` : "New Payment Check"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <F label="Beneficiary Type">
              <Select value={form.beneficiary_type || "staff"} onValueChange={(v) => setForm({ ...form, beneficiary_type: v, beneficiary_name: "" })}>
                <SelectTrigger data-testid="check-beneficiary-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff Member</SelectItem>
                  <SelectItem value="non_staff">Non-Staff</SelectItem>
                </SelectContent>
              </Select>
            </F>
            {form.beneficiary_type === "non_staff" ? (
              <F label="Beneficiary Name"><Input data-testid="check-beneficiary" placeholder="Type name" value={form.beneficiary_name} onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })} /></F>
            ) : (
              <F label="Staff Member">
                <Select value={employees.find((e) => e.name === form.beneficiary_name)?.id || ""}
                  onValueChange={(v) => { const emp = employees.find((e) => e.id === v); if (emp) setForm((f) => ({ ...f, beneficiary_name: emp.name, beneficiary_email: emp.email || f.beneficiary_email, beneficiary_phone: emp.phone || f.beneficiary_phone })); }}>
                  <SelectTrigger data-testid="check-staff-select"><SelectValue placeholder="Choose staff…" /></SelectTrigger>
                  <SelectContent>
                    {employees.length === 0 && <SelectItem value="none" disabled>No staff found</SelectItem>}
                    {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </F>
            )}
            <F label="Beneficiary Email (optional)">
              <Input data-testid="check-beneficiary-email" type="email" placeholder="vendor@email.com" value={form.beneficiary_email} onChange={(e) => setForm({ ...form, beneficiary_email: e.target.value })} className={emailErr ? "border-red-400" : ""} />
              {emailErr && <p className="text-xs text-red-600" data-testid="check-beneficiary-email-error">{emailErr}</p>}
            </F>
            <F label="Beneficiary Phone (for SMS alerts)">
              <PhoneField value={form.beneficiary_phone} onChange={(v) => setForm({ ...form, beneficiary_phone: v })} testId="check-beneficiary-phone" invalid={!!phoneErr} />
              {phoneErr && <p className="text-xs text-red-600" data-testid="check-beneficiary-phone-error">{phoneErr}</p>}
            </F>
            <F label="Amount (FCFA)"><Input data-testid="check-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} /></F>
            <div className="sm:col-span-2"><F label="Purpose">
              <Select value={form.purpose || ""} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="check-purpose"><SelectValue placeholder="Select purpose" /></SelectTrigger>
                <SelectContent>
                  {purposes.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </F></div>
            <F label="Debited Project *">
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="check-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>
                  {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Date (due / expected)"><Input data-testid="check-due" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></F>
            <F label="Intended Payment Method">
              <Select value={form.payment_method || "unset"} onValueChange={(v) => setForm({ ...form, payment_method: v === "unset" ? "" : v })}>
                <SelectTrigger data-testid="check-method"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Not specified</SelectItem>
                  {methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <div className="sm:col-span-2"><F label="Notes"><Textarea data-testid="check-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></F></div>
            <div className="sm:col-span-2">
              <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input type="checkbox" data-testid="check-recurring" checked={!!form.recurring}
                  onChange={(e) => setForm({ ...form, recurring: e.target.checked })} className="h-4 w-4 rounded border-slate-300" />
                <Repeat className="h-4 w-4 text-violet-600" /> Auto-raise this check every month
              </label>
              {form.recurring && <p className="text-xs text-slate-400 mt-1">A fresh pending check is created automatically each month from the due date.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="check-submit" onClick={save} disabled={!form.beneficiary_name || !form.purpose} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">{editing ? "Save Changes" : "Issue Check"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completeFor} onOpenChange={(o) => !o && setCompleteFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Complete {completeFor?.check_number}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500" data-testid="complete-date-note">The payment date is set automatically to today when this check is completed.</p>
            <F label="Payment Method">
              <Select value={payInfo.payment_method || "unset"} onValueChange={(v) => setPayInfo({ ...payInfo, payment_method: v === "unset" ? "" : v })}>
                <SelectTrigger data-testid="complete-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unset">Select method</SelectItem>
                  {methods.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Teller / Handler"><Input data-testid="complete-teller" value={payInfo.teller} onChange={(e) => setPayInfo({ ...payInfo, teller: e.target.value })} /></F>
            {completeFor?.project_name && <p className="text-xs text-slate-500">An expenditure of {fmtFCFA(completeFor?.amount)} will be recorded on <b>{completeFor?.project_name}</b>.</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteFor(null)}>Cancel</Button>
            <Button data-testid="complete-submit" onClick={doComplete} className="bg-emerald-600 hover:bg-emerald-700">Mark Completed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewFor} onOpenChange={(o) => !o && setViewFor(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="view-check-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2">{viewFor?.check_number}
            {viewFor && <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS[viewFor.status]?.cls}`}>{STATUS[viewFor.status]?.label}</span>}
          </DialogTitle></DialogHeader>
          {viewFor && (
            <div className="mt-1">
              <DRow label="Beneficiary">{viewFor.beneficiary_name}</DRow>
              {viewFor.beneficiary_email && <DRow label="Email">{viewFor.beneficiary_email}</DRow>}
              {viewFor.beneficiary_phone && <DRow label="Phone">{viewFor.beneficiary_phone}</DRow>}
              <DRow label="Purpose">{viewFor.purpose}</DRow>
              <DRow label="Debited Project">{viewFor.project_name || "—"}</DRow>
              <DRow label="Amount">{fmtFCFA(viewFor.amount)}</DRow>
              <DRow label="Issued By">{viewFor.issued_by || "—"}</DRow>
              <DRow label="Date Issued">{fmtDate(viewFor.date_issued)}</DRow>
              <DRow label="Due Date">{viewFor.due_date ? fmtDate(viewFor.due_date) : "—"}</DRow>
              <DRow label="Payment Date">{viewFor.payment_date ? fmtDate(viewFor.payment_date) : "—"}</DRow>
              <DRow label="Payment Method">{viewFor.payment_method || "—"}</DRow>
              <DRow label="Teller / Handler">{viewFor.teller || "—"}</DRow>
              <DRow label="Repeats Monthly">{viewFor.recurring ? `Yes · next ${viewFor.recurring_next || "—"}` : "No"}</DRow>
              {viewFor.notes && <DRow label="Notes">{viewFor.notes}</DRow>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewFor(null)}>Close</Button>
            <Button data-testid="view-print-btn" onClick={() => printPdf(viewFor)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Printer className="h-4 w-4 mr-2" /> Print PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DRow({ label, children }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
      <span className="text-xs uppercase tracking-wide text-slate-400">{label}</span>
      <span className="text-sm text-slate-800 text-right font-medium">{children}</span>
    </div>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>;
}
